import { useQuery } from '@tanstack/react-query';
import {
  checkCompanySMTP,
  getAllCompanies,
  getMySmtpStatus
} from '../services/api/implementation/impl';
import {
  COMPANY_SMTP_STATUSES_QUERY_KEY,
  MY_SMTP_STATUS_QUERY_KEY,
  companySmtpAddress,
  isCompanySmtpConnected,
  sendingSummary
} from '../utils/emailSendersAccess';

/**
 * Every address this builder sends email from: one SMTP config per company,
 * plus the logged-in user's personal Gmail.
 *
 * Shared by the Integrations card, the Outbound tab and the Add inbox form (to
 * spot an address that already has an app password saved), so all three read
 * the same cache entries. The companies key matches the one the Integrations
 * page and the old Mail Companies page already used.
 */
export function useEmailSenders({ enabled = true } = {}) {
  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies,
    enabled
  });

  const companies = Array.isArray(companiesQuery.data) ? companiesQuery.data : [];
  const companyIds = companies.map((company) => company.id);

  // One request per company, as before — there is no bulk endpoint.
  const statusesQuery = useQuery({
    queryKey: [COMPANY_SMTP_STATUSES_QUERY_KEY, companyIds],
    queryFn: async () => {
      const entries = await Promise.all(
        companyIds.map(async (id) => [id, await checkCompanySMTP(id)])
      );
      return Object.fromEntries(entries);
    },
    enabled: enabled && companyIds.length > 0
  });

  const personalQuery = useQuery({
    queryKey: MY_SMTP_STATUS_QUERY_KEY,
    queryFn: getMySmtpStatus,
    retry: false,
    enabled
  });

  const statuses = statusesQuery.data ?? {};
  const companySenders = companies.map((company) => {
    const status = statuses[company.id];
    return {
      company,
      connected: isCompanySmtpConnected(status),
      address: companySmtpAddress(status)
    };
  });

  const personal = personalQuery.isError ? null : (personalQuery.data ?? null);
  const personalConnected = Boolean(personal?.connected);
  const companyConnected = companySenders.filter((sender) => sender.connected).length;

  /** Flat list for address matching: `[{ address, name, kind }]`. */
  const senderAddresses = [
    ...companySenders
      .filter((sender) => sender.connected && sender.address)
      .map((sender) => ({
        address: sender.address,
        name: sender.company.company_name,
        kind: 'company'
      })),
    ...(personalConnected && personal?.fromEmail
      ? [{ address: personal.fromEmail, name: personal.fromName || 'You', kind: 'personal' }]
      : [])
  ];

  return {
    companies,
    companySenders,
    personal,
    personalConnected,
    companyConnected,
    senderAddresses,
    summary: sendingSummary({
      companyTotal: companies.length,
      companyConnected,
      personalConnected
    }),
    isPending:
      companiesQuery.isPending ||
      (companyIds.length > 0 && statusesQuery.isPending) ||
      personalQuery.isPending,
    statusesPending: companyIds.length > 0 && statusesQuery.isPending,
    personalPending: personalQuery.isPending
  };
}

export default useEmailSenders;
